import { useEffect, useState } from 'react'
import { isRouteErrorResponse, useNavigation, useRouteError } from 'react-router'
import { Button, Content, Page, PageHeader, Spinner } from '@/ui'

export function RouteLoading () {
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100dvh' }}>
      <Spinner />
    </div>
  )
}

/** Wait this long before showing the bar, so instant navigations don't flash it. */
const PROGRESS_DELAY_MS = 150
/** How long the filled bar stays before it is removed (covers the fade-out). */
const PROGRESS_FINISH_MS = 400

/**
 * Top-of-window progress bar while a navigation is pending: every route's
 * loader awaits auth, cities and sometimes teams (router/guard.ts), and lazy
 * pages load their chunk, so the old page otherwise sits there unchanged.
 */
export function NavigationProgress () {
  const busy = useNavigation().state !== 'idle'
  const [phase, setPhase] = useState<'hidden' | 'loading' | 'done'>('hidden')

  useEffect(() => {
    if (busy) {
      const t = window.setTimeout(() => setPhase('loading'), PROGRESS_DELAY_MS)
      return () => window.clearTimeout(t)
    }
    setPhase((p) => (p === 'loading' ? 'done' : 'hidden'))
  }, [busy])

  useEffect(() => {
    if (phase !== 'done') return
    const t = window.setTimeout(() => setPhase('hidden'), PROGRESS_FINISH_MS)
    return () => window.clearTimeout(t)
  }, [phase])

  if (phase === 'hidden') return null
  return (
    <div
      className={`nav-progress${phase === 'done' ? ' nav-progress--done' : ''}`}
      role="progressbar"
      aria-label="Loading page"
    />
  )
}

export function RouteError () {
  const error = useRouteError()
  console.error('Route error:', error)
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'Something went wrong.'
  return (
    <Page>
      <PageHeader title="Something went wrong" />
      <Content padding>
        <p>{message}</p>
        <Button onClick={() => window.location.assign('/')}>Back to home</Button>
      </Content>
    </Page>
  )
}
